import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import { API_BACKEND_URL } from "../Config/Config";

/**
 * The Excel review round trip: download test cases as a workbook, edit them in Excel, upload it back.
 *
 * The upload defaults to a dry run so the reviewer sees exactly what would change before anything
 * is written.
 */

/** Pulls the filename the server chose out of Content-Disposition. */
const fileNameFrom = (headers, fallback) => {
  const disposition = headers?.["content-disposition"] || "";
  const utf8 = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8) return decodeURIComponent(utf8[1]);
  const plain = disposition.match(/filename="?([^";]+)"?/i);
  return plain ? plain[1] : fallback;
};

export const downloadTestCaseWorkbook = async ({ collectionId, endpointId, companyId }) => {
  const scope = collectionId ? `collectionId=${collectionId}` : `endpointId=${endpointId}`;
  const response = await axios.get(
    `${API_BACKEND_URL}/testcase/excel/export?${scope}&companyId=${companyId}`,
    { responseType: "blob" },
  );

  const name = fileNameFrom(response.headers, "test-cases.xlsx");
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  return name;
};

export const useExportTestCaseWorkbook = (options = {}) =>
  useMutation({ mutationFn: downloadTestCaseWorkbook, ...options });

export const uploadTestCaseWorkbook = async ({
  file,
  companyId,
  dryRun = true,
  deleteMissing = false,
}) => {
  const form = new FormData();
  form.append("file", file);

  try {
    const response = await axios.post(
      `${API_BACKEND_URL}/testcase/excel/import?companyId=${companyId}`
        + `&dryRun=${dryRun}&deleteMissing=${deleteMissing}`,
      form,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return response.data;
  } catch (error) {
    // A workbook that fails validation comes back as 422 with the full report - that is a result
    // to show the reviewer, not a transport failure to swallow.
    const body = error?.response?.data;
    if (body && typeof body === "object" && "errors" in body) return body;
    throw error;
  }
};

export const useImportTestCaseWorkbook = (options = {}) =>
  useMutation({ mutationFn: uploadTestCaseWorkbook, ...options });
